import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { sellersAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { Search, Eye, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';

const STATUS_FILTERS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED'];

// ── Data masking helpers ──────────────────────────────────────────────
function maskEmail(email) {
  if (!email) return '—';
  const [local, domain] = email.split('@');
  if (!domain) return email;
  const [domainName, ...tld] = domain.split('.');
  const m = s => s.length <= 2 ? s : s[0] + '•'.repeat(Math.min(s.length - 2, 6)) + s[s.length - 1];
  return m(local) + '@' + m(domainName) + '.' + tld.join('.');
}
function maskPhone(phone) {
  if (!phone) return '—';
  const p = String(phone);
  if (p.length <= 4) return p;
  return p.slice(0, 2) + '•'.repeat(Math.min(p.length - 4, 6)) + p.slice(-2);
}

// ── Sort icon helper ──────────────────────────────────────────────────
function SortIcon({ column, sortConfig }) {
  const active = sortConfig.key === column;
  const isAsc = active && sortConfig.direction === 'asc';
  const isDesc = active && sortConfig.direction === 'desc';

  return (
    <span style={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 7,
      verticalAlign: 'middle',
      gap: 2,
      lineHeight: 1,
      padding: '2px 3px',
      borderRadius: 4,
      background: active ? 'rgba(34,197,94,0.12)' : 'transparent',
      transition: 'background 0.15s',
    }}>
      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
        <path d="M4.5 0.5L8.5 6.5H0.5L4.5 0.5Z"
          fill={isAsc ? '#16a34a' : '#9ca3af'}
          stroke={isAsc ? '#16a34a' : 'transparent'}
          strokeWidth="0.5"
          style={{ transition: 'fill 0.15s' }}
        />
      </svg>
      <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
        <path d="M4.5 6.5L0.5 0.5H8.5L4.5 6.5Z"
          fill={isDesc ? '#16a34a' : '#9ca3af'}
          stroke={isDesc ? '#16a34a' : 'transparent'}
          strokeWidth="0.5"
          style={{ transition: 'fill 0.15s' }}
        />
      </svg>
    </span>
  );
}

SortIcon.propTypes = {
  column: PropTypes.string.isRequired,
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']),
  }).isRequired,
};

function ReasonModal({ title, onConfirm, onCancel, loading }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onCancel}><XCircle size={16} /></button>
        </div>
        <div className="form-group">
          <label>Reason</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Enter reason..." />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(reason)} disabled={loading || !reason.trim()}>
            {loading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

ReasonModal.propTypes = {
  title: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

function SellerDetailModal({ seller, onClose, onAction }) {
  if (!seller) return null;
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span className="modal-title">Seller Details</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><XCircle size={16} /></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20, padding: '14px', background: 'var(--bg-hover)', borderRadius: 10 }}>
          <div className="avatar lg">{seller.businessName?.[0]}</div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{seller.businessName}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{seller.ownerName}</div>
            <span className={`badge ${seller.status?.toLowerCase()}`} style={{ marginTop: 6 }}>{seller.status}</span>
          </div>
        </div>
        <div>
          {[
            ['Email', maskEmail(seller.email)],
            ['Phone', maskPhone(seller.phone)],
            ['Address', seller.address],
            ['FSSAI License', seller.fssaiLicense || 'Not provided'],
            ['Registered', seller.createdAt ? new Date(seller.createdAt).toLocaleDateString('en-IN') : '—'],
            ['Rejection Reason', seller.rejectionReason || '—'],
          ].map(([k, v]) => (
            <div className="detail-row" key={k}>
              <span className="detail-label">{k}</span>
              <span className="detail-value" style={{ textAlign: 'right', maxWidth: '60%' }}>{v}</span>
            </div>
          ))}
        </div>
        <div className="modal-footer" style={{ marginTop: 20 }}>
          {seller.status === 'PENDING' && (
            <>
              <button className="btn btn-success" onClick={() => onAction('approve', seller)}>
                <CheckCircle size={14} /> Approve
              </button>
              <button className="btn btn-danger" onClick={() => onAction('reject', seller)}>
                <XCircle size={14} /> Reject
              </button>
            </>
          )}
          {seller.status === 'APPROVED' && (
            <button className="btn btn-danger" onClick={() => onAction('suspend', seller)}>
              <AlertCircle size={14} /> Suspend
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

SellerDetailModal.propTypes = {
  seller: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    businessName: PropTypes.string,
    ownerName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    address: PropTypes.string,
    fssaiLicense: PropTypes.string,
    createdAt: PropTypes.string,
    rejectionReason: PropTypes.string,
    status: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
};

SellerDetailModal.defaultProps = {
  seller: null,
};

export default function Sellers() {
  // ── Desktop state (pagination) ────────────────────────────────────
  const [sellers, setSellers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Mobile state (lazy / infinite scroll) ────────────────────────
  const [mobileItems, setMobileItems] = useState([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [mobileHasMore, setMobileHasMore] = useState(true);
  const [mobileLoading, setMobileLoading] = useState(false);
  // sentinel ref that IntersectionObserver watches
  const sentinelRef = useRef(null);

  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const toast = useToast();

  // ── Desktop load (pagination) ─────────────────────────────────────
  const loadDesktop = useCallback(async () => {
    setLoading(true);
    try {
      const fn = activeTab === 'pending' ? sellersAPI.getPending : sellersAPI.getAll;
      const res = await fn(page, 10);
      setSellers(res.data.content || []);
      setTotalPages(res.data.totalPages || 0);
      setTotal(res.data.totalElements || 0);
    } catch {
      toast('Failed to load sellers', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDesktop(); }, [loadDesktop]);

  // ── Mobile load (append pages) ────────────────────────────────────
  const loadMobilePage = useCallback(async (pageNum, reset = false) => {
    if (mobileLoading) return;
    setMobileLoading(true);
    try {
      const fn = activeTab === 'pending' ? sellersAPI.getPending : sellersAPI.getAll;
      const res = await fn(pageNum, 10);
      const content = res.data.content || [];
      const tp = res.data.totalPages || 0;
      setMobileItems(prev => reset ? content : [...prev, ...content]);
      setMobileHasMore(pageNum + 1 < tp);
    } catch {
      toast('Failed to load sellers', 'error');
    } finally {
      setMobileLoading(false);
    }
  }, [activeTab, mobileLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset mobile list when tab changes
  useEffect(() => {
    setMobileItems([]);
    setMobilePage(0);
    setMobileHasMore(true);
    loadMobilePage(0, true);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver — fires when sentinel enters viewport
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && mobileHasMore && !mobileLoading) {
          const nextPage = mobilePage + 1;
          setMobilePage(nextPage);
          loadMobilePage(nextPage);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [mobileHasMore, mobileLoading, mobilePage, loadMobilePage]);

  // ── Toggle sort ───────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'desc') return { key: null, direction: 'asc' };
        return { key, direction: 'desc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleAction = (type, seller) => {
    setSelected(null);
    if (type === 'approve') {
      confirmAction('approve', seller, '');
    } else {
      setActionModal({ type, seller });
    }
  };

  const confirmAction = async (type, seller, reason) => {
    setActionLoading(true);
    try {
      if (type === 'approve') await sellersAPI.approve(seller.id);
      else if (type === 'reject') await sellersAPI.reject(seller.id, reason);
      else if (type === 'suspend') await sellersAPI.suspend(seller.id, reason);
      const successMsg = type === 'suspend'
        ? 'Seller suspended — all active orders have been cancelled'
        : `Seller ${type}d successfully`;
      toast(successMsg, 'success');
      setActionModal(null);
      // Refresh both views
      loadDesktop();
      setMobileItems([]);
      setMobilePage(0);
      setMobileHasMore(true);
      loadMobilePage(0, true);
    } catch {
      toast(`Failed to ${type} seller`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Tiered search ─────────────────────────────────────────────────
  const tieredScore = (s, query) => {
    const words = query.split(/\s+/).filter(Boolean);
    const name  = (s.businessName || '').toLowerCase();
    const owner = (s.ownerName || '').toLowerCase();
    const email = (s.email || '').toLowerCase();
    let sc = 0;
    if (name === query)          sc += 1000;
    if (name.startsWith(query))  sc += 500;
    else if (words.every(w => name.includes(w))) sc += 400;
    if (email.startsWith(query)) sc += 300;
    words.forEach(w => {
      if (name.includes(w))  sc += 50;
      if (owner.includes(w)) sc += 40;
      if (email.includes(w)) sc += 30;
    });
    if ((s.rating || 0) > 0) sc += s.rating * 10;
    return sc;
  };

  // Desktop filtered + sorted
  const baseFiltered = sellers.filter(s => filter === 'ALL' || s.status === filter);
  const filtered = search
    ? baseFiltered.map(s => ({ s, sc: tieredScore(s, search.toLowerCase()) }))
        .filter(x => x.sc > 0)
        .sort((a, b) => b.sc - a.sc)
        .map(x => x.s)
    : baseFiltered;

  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal, bVal;
    if (sortConfig.key === 'businessName') {
      aVal = (a.businessName || '').toLowerCase();
      bVal = (b.businessName || '').toLowerCase();
    } else if (sortConfig.key === 'createdAt') {
      aVal = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      bVal = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    }
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Mobile filtered (client-side on already loaded items)
  const mobileBaseFiltered = mobileItems.filter(s => filter === 'ALL' || s.status === filter);
  const mobileSorted = search
    ? mobileBaseFiltered.map(s => ({ s, sc: tieredScore(s, search.toLowerCase()) }))
        .filter(x => x.sc > 0)
        .sort((a, b) => b.sc - a.sc)
        .map(x => x.s)
    : mobileBaseFiltered;

  const sortableTh = {
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  return (
    <Layout title="Sellers" subtitle="Manage and review seller accounts">
      {/* Tabs */}
      <div className="tabs" style={{ width: 'fit-content', marginBottom: 20 }}>
        <button className={`tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => { setActiveTab('all'); setPage(0); }}>All Sellers</button>
        <button className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => { setActiveTab('pending'); setPage(0); }}>Pending Approval</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 18, flexWrap: 'nowrap', width: '100%', minWidth: 0 }}>
        <div className="search-box" style={{ flex: 1, minWidth: 0 }}>
          <Search size={14} className="search-icon" />
          <input placeholder="Search by name, email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {activeTab === 'all' && (
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{ flexShrink: 0, height: 36, borderRadius: 8, border: '1px solid var(--border)', padding: '0 8px', fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-card)', cursor: 'pointer', maxWidth: 130 }}
          >
            {STATUS_FILTERS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
      </div>

      {/* ── MOBILE: infinite scroll card list ── */}
      <div className="sellers-mobile-list">
        {mobileLoading && mobilePage === 0 ? (
          <div className="page-loader"><div className="spinner" /></div>
        ) : mobileSorted.length === 0 && !mobileLoading ? (
          <div className="empty-state" style={{ marginTop: 40 }}>
            <h3>No sellers found</h3>
            <p>Try a different filter or search term</p>
          </div>
        ) : (
          <>
            {mobileSorted.map(seller => (
              <button key={seller.id} className="seller-card" type="button" onClick={() => setSelected(seller)} style={{background:"none",border:"none",padding:0,cursor:"pointer",display:"block",width:"100%",textAlign:"left"}}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="avatar" style={{ width: 42, height: 42, fontSize: 16, flexShrink: 0 }}>
                    {seller.businessName?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {seller.businessName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{seller.ownerName}</div>
                  </div>
                  <span className={`badge ${seller.status?.toLowerCase()}`} style={{ flexShrink: 0 }}>{seller.status}</span>
                </div>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 54 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--mono)' }}>{maskEmail(seller.email)}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8, paddingLeft: 54 }} onClick={e => e.stopPropagation()}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelected(seller)} style={{ fontSize: 12 }}>
                    <Eye size={13} /> View
                  </button>
                  {seller.status === 'PENDING' && (
                    <>
                      <button className="btn btn-success btn-sm" onClick={() => handleAction('approve', seller)} style={{ fontSize: 12 }}>
                        <CheckCircle size={13} /> Approve
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => setActionModal({ type: 'reject', seller })} style={{ fontSize: 12 }}>
                        <XCircle size={13} /> Reject
                      </button>
                    </>
                  )}
                  {seller.status === 'APPROVED' && (
                    <button className="btn btn-danger btn-sm" onClick={() => setActionModal({ type: 'suspend', seller })} style={{ fontSize: 12 }}>
                      Suspend
                    </button>
                  )}
                  {seller.status === 'SUSPENDED' && (
                    <button className="btn btn-success btn-sm" onClick={() => handleAction('approve', seller)} style={{ fontSize: 12 }}>
                      <CheckCircle size={13} /> Re-Approve
                    </button>
                  )}
                </div>
              </button>
            ))}

            {/* Sentinel div — IntersectionObserver watches this */}
            <div ref={sentinelRef} style={{ height: 1 }} />

            {/* Loading spinner for next page */}
            {mobileLoading && mobilePage > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                <div className="spinner" />
              </div>
            )}

            {/* End of list message */}
            {!mobileHasMore && mobileSorted.length > 0 && (
              <div style={{ textAlign: 'center', padding: '16px 0 8px', fontSize: 12, color: 'var(--text-muted)' }}>
               All sellers are loaded! ({mobileSorted.length})
              </div>
            )}
          </>
        )}
      </div>

      {/* ── DESKTOP: table with pagination ── */}
      <div className="sellers-desktop-table card">
        <div className="table-wrap" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 860, width: '100%' }}>
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 130 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 160 }} />
            </colgroup>
            <thead>
              <tr>
                <th style={sortableTh} onClick={() => handleSort('businessName')} title="Click to sort by business name">
                  Business <SortIcon column="businessName" sortConfig={sortConfig} />
                </th>
                <th>Owner</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th style={sortableTh} onClick={() => handleSort('createdAt')} title="Click to sort by registration date">
                  Registered <SortIcon column="createdAt" sortConfig={sortConfig} />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: 'auto' }} /></td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7}><div className="empty-state"><h3>No sellers found</h3><p>Try a different filter or search term</p></div></td></tr>
              ) : sorted.map(seller => (
                <tr key={seller.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="avatar">{seller.businessName?.[0]}</div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{seller.businessName}</span>
                    </div>
                  </td>
                  <td>{seller.ownerName}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                    <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{maskEmail(seller.email)}</span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                    <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>{maskPhone(seller.phone)}</span>
                  </td>
                  <td><span className={`badge ${seller.status?.toLowerCase()}`}>{seller.status}</span></td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setSelected(seller)} title="View"><Eye size={14} /></button>
                      {seller.status === 'PENDING' && (
                        <>
                          <button className="btn btn-success btn-sm" onClick={() => handleAction('approve', seller)}><CheckCircle size={13} /></button>
                          <button className="btn btn-danger btn-sm" onClick={() => setActionModal({ type: 'reject', seller })}><XCircle size={13} /></button>
                        </>
                      )}
                      {seller.status === 'APPROVED' && (
                        <button className="btn btn-danger btn-sm" onClick={() => setActionModal({ type: 'suspend', seller })}>Suspend</button>
                      )}
                      {seller.status === 'SUSPENDED' && (
                        <button className="btn btn-success btn-sm" onClick={() => handleAction('approve', seller)}>
                          <CheckCircle size={13} /> Re-Approve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>Showing {sorted.length} of {total} sellers</span>
          <div className="pagination-controls">
            <button className="btn btn-ghost btn-sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {page + 1} of {Math.max(1, totalPages)}</span>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {selected && <SellerDetailModal seller={selected} onClose={() => setSelected(null)} onAction={handleAction} />}
      {actionModal && (
        <ReasonModal
          title={`${actionModal.type === 'reject' ? 'Reject' : 'Suspend'} "${actionModal.seller.businessName}"?`}
          onConfirm={(reason) => confirmAction(actionModal.type, actionModal.seller, reason)}
          onCancel={() => setActionModal(null)}
          loading={actionLoading}
        />
      )}
    </Layout>
  );
}